import * as authApi from '@/api/auth';
import { moduleCatalog } from '@/router/module-catalog';
import { buildSidebarMenus, createActionMap } from '@/utils/permission';
import * as defaultStorage from '@/utils/storage';

function createEmptyState() {
  return {
    token: '',
    user: {
      id: null,
      account: '',
      nickname: '',
      avatar: ''
    },
    permissions: [],
    sidebarMenus: [],
    actionMap: {}
  };
}

function normalizeSession(data, catalog) {
  const permissions = Array.isArray(data.permission) ? data.permission : data.permissions || [];
  const sidebarMenus = buildSidebarMenus(catalog, permissions);

  return {
    token: data.token || '',
    user: data.user || createEmptyState().user,
    permissions,
    sidebarMenus,
    actionMap: createActionMap(permissions, catalog)
  };
}

function firstRoute(menus) {
  return '/dashboard';
}

export function createAuthModule(deps = {}) {
  const {
    authApi: injectedAuthApi = authApi,
    storage = defaultStorage,
    moduleCatalog: catalog = moduleCatalog
  } = deps;

  return {
    namespaced: true,
    state: createEmptyState,
    mutations: {
      setSession(state, payload) {
        Object.assign(state, normalizeSession(payload, catalog));
      },
      resetSession(state) {
        Object.assign(state, createEmptyState());
      },
      setUser(state, user) {
        state.user = {
          ...state.user,
          ...user
        };
      }
    },
    actions: {
      async bootstrap({ commit }) {
        const session = storage.getSession();

        if (!session) {
          commit('resetSession');
          return '/login';
        }

        commit('setSession', session);
        return firstRoute(buildSidebarMenus(catalog, session.permissions || []));
      },
      async login({ commit }, payload) {
        const response = await injectedAuthApi.login(payload);
        const session = normalizeSession(response.data || {}, catalog);

        storage.setSession({
          token: session.token,
          user: session.user,
          permissions: session.permissions
        });
        commit('setSession', {
          token: session.token,
          user: session.user,
          permission: session.permissions
        });

        return firstRoute(session.sidebarMenus);
      },
      async logout({ commit }) {
        storage.clearSession();
        commit('resetSession');
      },
      async updateAvatar({ state, commit }, avatar) {
        commit('setUser', {
          avatar
        });
        defaultStorage.setSession({
          token: state.token,
          user: {
            ...state.user,
            avatar
          },
          permissions: state.permissions
        });
      }
    }
  };
}

export default createAuthModule();
